import type { HTMLAttributes, ReactElement } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

// Underlying UI component for text input.
import type { SDSTextType } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';

// Shared props type provided by the Controller wrapper (BaseFormField).
import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Text Input component. */
export interface BaseFormTextInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed for the input. */
  label: string;
  /** Indicates if the field is mandatory. */
  isRequired?: boolean;
  /** Placeholder text displayed when the input is empty. */
  placeholder?: string;
  /** The type of the input element (e.g., 'text', 'password'). */
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';
  /** Optional icon element to display on the left side of the input. */
  leftIcon?: ReactElement;
  /** Optional icon element to display on the right side of the input. */
  rightIcon?: ReactElement;
  /** Optional text or symbol prefix displayed inside the input before the value. */
  prefix?: string;
  /** Data attribute for testing purposes. */
  dataTestId?: string;
  /** Data attribute for testing error message. */
  errorMessageTestId?: string;
  /** Maximum length of input value. */
  maxLength?: number;
  /** Typography of the label and of the entered text. */
  labelTextType?: SDSTextType;
  textType?: SDSTextType;
  /** Browser autofill hint (WCAG 1.3.5), e.g. `given-name` or `email`. */
  autoComplete?: string;
  /** Virtual keyboard hint, e.g. `email`. */
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
}

/** Combined props for the FormTextInput component, including RHF control props. */
interface FormTextInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends BaseFormTextInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {
  // Includes field, errorMessage, isDisabled
}

/**
 * Renders an SDSTextInput component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormTextInput<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>({
  field, // RHF field object { value, onChange, onBlur, name, ref }
  errorMessage,
  isDisabled,
  label,
  isRequired = false,
  placeholder,
  type,
  leftIcon,
  rightIcon,
  prefix,
  dataTestId,
  errorMessageTestId,
  maxLength,
  autoComplete,
  inputMode,
  labelTextType,
  textType,
}: FormTextInputProps<TFieldValues, TName>) {
  // Renders the underlying SDSTextInput, spreading the RHF field object
  // and passing down specific configuration and state props.
  return (
    <SDSTextInput
      {...field}
      label={label}
      errorMessage={errorMessage}
      errorMessageTestId={errorMessageTestId}
      isRequired={isRequired}
      placeholder={placeholder}
      isDisabled={isDisabled}
      type={type}
      isFullWidth
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      prefix={prefix}
      dataTestId={dataTestId}
      maxLength={maxLength}
      autoComplete={autoComplete}
      inputMode={inputMode}
      labelTextType={labelTextType}
      textType={textType}
    />
  );
}
